/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from 'react';
import { VITE_DATA_VORTEX_API_URL } from '../configs/config';
import useMeeting from 'hooks/useMeeting';
import { datadogLogs } from '@datadog/browser-logs';
import type { IMeetingParticipant } from 'adapters/MeetingAdapter';
import { vadCheckSpeech, trimAudio } from './vadTrimmer';
import { encodeOpus, isOpusEncodingSupported } from './opusEncoder';
import { useClientCapturedParticipants } from './useClientCapturedParticipants';

// Configuration constants
const CONFIG = {
  SAMPLE_RATE: 48000,
  BUFFER_SIZE: 48000 * 10,
  API_ENDPOINT: VITE_DATA_VORTEX_API_URL || 'http://localhost:9000',
  DEBUG: false,
  ENABLE_VAD: true,
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
};

// Type definitions
interface AudioResources {
  context: AudioContext;
  source: MediaStreamAudioSourceNode;
  processor: AudioWorkletNode;
  track: MediaStreamTrack;
}

// Logger functions
const logger = {
  info: (message: string, data?: any) => {
    if (!CONFIG.DEBUG) return;
    datadogLogs.logger.info(
      `TalkTime Info debug log: ${message}`,
      data,
    );
  },
  error: (message: string, error?: any) => {
    datadogLogs.logger.error(
      'TalkTime Error',
      { message },
      error as Error,
    );
  },
};

// Downsample Float32 audio from one sample rate to another (averaging filter).
const downsample = (input: Float32Array, fromRate: number, toRate: number): Float32Array => {
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    let sum = 0;
    const base = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    for (let j = base; j < end; j++) sum += input[j];
    out[i] = sum / (end - base);
  }
  return out;
};

// Encode a mono Int16 PCM array as a WAV file buffer (44-byte RIFF header + samples).
const encodeWAV = (samples: Int16Array, sampleRate: number): ArrayBuffer => {
  const buf = new ArrayBuffer(44 + samples.byteLength);
  const v = new DataView(buf);
  const w = (off: number, s: string) =>
    s.split('').forEach((c, i) => v.setUint8(off + i, c.charCodeAt(0)));
  w(0, 'RIFF'); v.setUint32(4, 36 + samples.byteLength, true);
  w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true);        // PCM subchunk size
  v.setUint16(20, 1, true);         // PCM format
  v.setUint16(22, 1, true);         // mono
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true); // byte rate (16-bit mono)
  v.setUint16(32, 2, true);         // block align
  v.setUint16(34, 16, true);        // bits per sample
  w(36, 'data'); v.setUint32(40, samples.byteLength, true);
  new Uint8Array(buf, 44).set(new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength));
  return buf;
};

// Worker management - define as a module singleton to ensure only one worker exists
let audioWorker: Worker | null = null;

const getAudioWorker = (): Worker => {
  if (!audioWorker) {
    audioWorker = new Worker(new URL('./audioProcessingWorker.js', import.meta.url));

    // Handle cleanup on page unload
    window.addEventListener('beforeunload', () => {
      if (audioWorker) {
        audioWorker.terminate();
        audioWorker = null;
      }
    });
  }
  return audioWorker;
};

// Audio processing hook with Worker-based queue system
export const useStudentTalkTime = () => {
  const meetingContext = useMeeting();
  const { meeting, classId, joinedParticipants } = meetingContext;
  // Participants transcribing their own mic client-side (tutors on
  // lms-frontend's useTutorTalkTime). Skipped here while they keep announcing.
  const isClientCaptured = useClientCapturedParticipants();

  // Track resources that need cleanup
  const activeResources = useRef(new Map<string, AudioResources>());

  // Track which participants we've already set up handlers for
  const handledParticipants = useRef(new Set<string>());

  // Track audio update handlers
  const audioUpdateHandlersRef = useRef(
    new Map<string, (payload: { audioEnabled: boolean; audioTrack: MediaStreamTrack }) => void>(),
  );

  // Worker reference
  const workerRef = useRef<Worker | null>(null);
  const participantIdMapping = useRef(new Map<string, string>());

  // Kept across renders: a fresh Set each render let two setups for the same
  // participant run at once.
  const initializingParticipants = useRef(new Set<string>()).current;

  // Create unique participant ID from Dyte participant
  const getParticipantId = (participant: IMeetingParticipant): string => {
    const userType = participant.presetName === 'group_call_host' ? 'tutor' : 'student';
    return `${userType}-${classId}-${participant.customParticipantId?.split('-').pop()}`;
  };

  // Clean up audio processing resources for a participant
  const cleanupAudioProcessing = useCallback(
    async (participantId: string): Promise<void> => {
      const resources = activeResources.current.get(participantId);
      if (!resources) return;

      try {
        // Disconnect the processor
        if (resources.processor) {
          try {
            resources.processor.disconnect();
            if (resources.processor.port) {
              resources.processor.port.close();
            }
          } catch (error) {
            // Ignore disconnection errors
          }
        }

        // Disconnect the source
        if (resources.source) {
          try {
            resources.source.disconnect();
          } catch (error) {
            // Ignore disconnection errors
          }
        }

        // Close the audio context - just check if it's already closed
        if (resources.context && resources.context.state !== 'closed') {
          try {
            await resources.context.close();
          } catch (error) {
            // Just log and continue
            logger.info('Error closing context', {
              error: error instanceof Error ? error.message : String(error),
              participantId,
              classId,
            });
          }
        }
      } catch (error) {
        // Log but don't let errors interrupt cleanup
        logger.error('Error during cleanup', {
          error: error instanceof Error ? error.message : String(error),
          participantId,
          classId,
        });
      } finally {
        // This is the critical part - ALWAYS clear the resources
        activeResources.current.delete(participantId);
      }
    },
    [classId],
  );

  // Process buffer via the worker queue system — sends raw Float32 as transferable ArrayBuffer
  const processBuffer = useCallback(
    (participantId: string, audioData: Float32Array, userType: string, userId: string, bufferTimestamp?: number): void => {
      if (!workerRef.current) return;

      const processingId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Transfer ownership of the underlying ArrayBuffer to avoid a copy
      const transferBuffer = audioData.buffer.slice(
        audioData.byteOffset,
        audioData.byteOffset + audioData.byteLength,
      );

      workerRef.current.postMessage(
        {
          action: 'enqueueAudio',
          participantId,
          processingId,
          audioData: transferBuffer,
          userType,
          userId,
          bufferTimestamp: bufferTimestamp || Date.now(),
        },
        [transferBuffer], // transfer, not copy
      );
    },
    [],
  );

  // Add these useCallback wrappers before the useEffect
  const getParticipantIdCallback = useCallback(
    (participant: IMeetingParticipant): string => {
      const userType = participant.presetName === 'group_call_host' ? 'tutor' : 'student';
      return `${userType}-${classId}-${participant.customParticipantId?.split('-').pop()}`;
    },
    [classId],
  );

  const setupAudioProcessingCallback = useCallback(
    async (
      participant: IMeetingParticipant,
      audioTrack: MediaStreamTrack,
      retryCount = 0,
    ): Promise<void> => {
      const participantId = getParticipantIdCallback(participant);
      const userType = participant.presetName === 'group_call_host' ? 'tutor' : 'student';

      // Their own client uploads their turns; capturing here too would
      // transcribe every turn twice.
      if (isClientCaptured(participant.customParticipantId)) return;

      // Log detailed debugging information
      logger.info('Audio processing setup attempt', {
        participantId,
        classId,
        name: participant.name,
        userType,
        audioEnabled: participant.audioEnabled,
        audioTrackExists: !!audioTrack,
        retryCount,
        alreadyInitializing: initializingParticipants.has(participantId),
        hasActiveProcessing: activeResources.current.has(participantId)
      });

      // Check if we're already initializing this participant
      if (initializingParticipants.has(participantId)) {
        logger.info('Already initializing audio for participant, skipping', {
          participantId,
          classId,
        });
        return;
      }

      // Mark as initializing
      initializingParticipants.add(participantId);

      try {
        // Validate audio track - Be more lenient with validation but ensure it exists
        if (!audioTrack) {
          logger.info('Audio track is null or undefined, may retry', {
            participantId,
            classId,
            userType
          });

          if (retryCount < CONFIG.MAX_RETRIES) {
            const delay = CONFIG.RETRY_DELAY * Math.pow(2, retryCount);
            setTimeout(() => {
              // Check again if participant exists and has audio enabled
              const currentParticipant = joinedParticipants.find(p => p.id === participant.id);
              if (currentParticipant?.audioEnabled && currentParticipant?.audioTrack) {
                logger.info('Retrying audio setup', {
                  participantId,
                  retryCount: retryCount + 1
                });
                void setupAudioProcessingCallback(
                  currentParticipant,
                  currentParticipant.audioTrack,
                  retryCount + 1,
                );
              } else {
                logger.info('Retry canceled - participant no longer valid', {
                  participantId
                });
              }
            }, delay);
          }
          return;
        }

        // Clean up any existing audio resources
        await cleanupAudioProcessing(participantId);

        // Create new audio context
        const context = new AudioContext({
          sampleRate: CONFIG.SAMPLE_RATE,
          latencyHint: 'interactive',
        });

        await context.resume();

        // Create the AudioWorklet processor script
        const moduleScript = `
        class TalkTimeProcessor extends AudioWorkletProcessor {
          constructor(options) {
            super();
            this.buffer = new Float32Array(${CONFIG.BUFFER_SIZE});
            this.position = 0;
            this.lastProcessTime = Date.now();
            this.bufferStartTimestamp = this.lastProcessTime;
            
            const processorOptions = options.processorOptions || {};
            this.participantId = processorOptions.participantId;
            this.userType = processorOptions.userType;
            this.userId = processorOptions.userId;
            this.classId = processorOptions.classId;
          }

          process(inputs, outputs) {
            try {
              const input = inputs[0]?.[0];
              if (!input?.length) {
                this.port.postMessage({ type: 'error', message: 'No input data' });
                return true;
              }

              const currentTime = Date.now();
              
              for (let i = 0; i < input.length; i++) {
                if (this.position < this.buffer.length) {
                  this.buffer[this.position++] = Math.max(-1, Math.min(1, input[i]));
                }
              }

              // When buffer is full or 10 seconds have passed
              if (this.position >= this.buffer.length || 
                  (currentTime - this.lastProcessTime >= 10000 && this.position > 0)) {
                
                this.port.postMessage({
                  buffer: this.buffer.slice(0, this.position),
                  participantId: this.participantId,
                  userType: this.userType,
                  userId: this.userId,
                  bufferTimestamp: this.bufferStartTimestamp
                });
                
                this.position = 0;
                this.lastProcessTime = currentTime;
                this.bufferStartTimestamp = currentTime;
              }
            } catch (error) {
              this.port.postMessage({ 
                type: 'error', 
                message: 'Error in audio processing', 
                error: error.message 
              });
            }
            return true;
          }
        }

      registerProcessor('talk-time-processor-${participantId}', TalkTimeProcessor);
    `;

        const blob = new Blob([moduleScript], { type: 'text/javascript' });
        const scriptUrl = URL.createObjectURL(blob);

        try {
          await context.audioWorklet.addModule(scriptUrl);
          const stream = new MediaStream([audioTrack]);
          const source = context.createMediaStreamSource(stream);

          if (source.mediaStream.getAudioTracks().length === 0) {
            throw new Error('No audio tracks available');
          }

          const processor = new AudioWorkletNode(context, `talk-time-processor-${participantId}`, {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            channelCount: 1,
            channelCountMode: 'explicit',
            channelInterpretation: 'discrete',
            processorOptions: {
              participantId,
              userType,
              userId: participant.customParticipantId,
              classId,
            },
          });

          processor.port.onmessage = (event) => {
            if (event.data.buffer) {
              // Their own client took over since this capture started: drop the
              // chunk here, before any upload, and stop capturing them.
              if (isClientCaptured(event.data.userId)) {
                void cleanupAudioProcessing(participantId);
                return;
              }
              processBuffer(
                event.data.participantId,
                new Float32Array(event.data.buffer),
                event.data.userType,
                event.data.userId,
                event.data.bufferTimestamp // Pass along the buffer timestamp
              );
            } else if (event.data.type === 'error') {
              logger.error('Processor reported error', {
                message: event.data.message,
                error: event.data.error,
                participantId,
                classId,
              });
            }
          };

          // Start the processor port
          await processor.port.start();

          processor.onprocessorerror = (err) => {
            logger.info('Processor error', { error: err, participantId, classId });
          };

          // Connect audio nodes
          source.connect(processor);

          // Store resources for cleanup
          activeResources.current.set(participantId, {
            context,
            source,
            processor,
            track: audioTrack,
          });

          logger.info('Audio processing successfully set up', {
            participantId,
            classId,
            userType,
          });

        } finally {
          URL.revokeObjectURL(scriptUrl);
        }
      } catch (error) {
        logger.error('Error setting up audio processing', {
          error: error instanceof Error ? error.message : String(error),
          participantId,
          classId,
        });

        // Retry on failure
        if (retryCount < CONFIG.MAX_RETRIES) {
          const delay = CONFIG.RETRY_DELAY * Math.pow(2, retryCount);
          setTimeout(() => {
            if (participant.audioEnabled && participant.audioTrack) {
              void setupAudioProcessingCallback(
                participant,
                participant.audioTrack,
                retryCount + 1,
              );
            }
          }, delay);
        }
      } finally {
        // Always remove from initializing set
        initializingParticipants.delete(participantId);
      }
    },
    [classId, cleanupAudioProcessing, processBuffer, getParticipantIdCallback, isClientCaptured],
  );

  // What the 5 s check below reads. The participant list is rebuilt on most
  // room events (an active-speaker change every few seconds while people
  // talk), and an interval keyed on it was torn down and restarted with every
  // rebuild, so the check only ran after 5 quiet seconds: a tutor whose client
  // had taken over stayed captured here for minutes. A steady timer reads the
  // latest values through this ref instead.
  const monitorRef = useRef({
    meeting,
    joinedParticipants,
    getParticipantIdCallback,
    setupAudioProcessingCallback,
    cleanupAudioProcessing,
    initializingParticipants,
  });
  monitorRef.current = {
    meeting,
    joinedParticipants,
    getParticipantIdCallback,
    setupAudioProcessingCallback,
    cleanupAudioProcessing,
    initializingParticipants,
  };

  // Regular monitoring to ensure all participants have proper audio setup
  useEffect(() => {
    // Check every 5 seconds that all participants have proper audio setup
    const intervalId = setInterval(() => {
      const {
        meeting,
        joinedParticipants,
        getParticipantIdCallback,
        setupAudioProcessingCallback,
        cleanupAudioProcessing,
        initializingParticipants,
      } = monitorRef.current;
      if (!meeting || !joinedParticipants?.length) return;
      joinedParticipants.forEach((participant) => {
        // Hand-off both ways: stop once a client starts announcing, and pick the
        // participant back up (below) once its announcements go stale.
        if (isClientCaptured(participant?.customParticipantId)) {
          const capturedId = getParticipantIdCallback(participant);
          if (activeResources.current.has(capturedId)) {
            logger.info('Participant captures client-side, stopping recorder capture', {
              participantId: capturedId,
              classId,
            });
            void cleanupAudioProcessing(capturedId);
          }
          return;
        }
        if (participant?.audioEnabled && participant?.audioTrack) {
          const participantId = getParticipantIdCallback(participant);

          // If this participant should have audio processing but doesn't, set it up
          if (!activeResources.current.has(participantId) &&
            !initializingParticipants.has(participantId)) {
            logger.info('Setup from monitoring interval', {
              participantId,
              classId,
              name: participant.name,
            });
            void setupAudioProcessingCallback(participant, participant.audioTrack, 0);
          }
        }
      });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isClientCaptured, classId]);

  // Add forced check for mute state changes without relying on events
  useEffect(() => {
    if (!meeting || !joinedParticipants?.length) return;

    // Keep track of participant audio states to detect changes even if events don't fire
    const audioStateMap = new Map<string, { audioEnabled: boolean, hasAudioTrack: boolean }>();

    const checkForMuteChanges = () => {
      joinedParticipants.forEach(participant => {
        if (!participant?.id || participant.id === (meeting as any).self.id) return;

        const participantId = getParticipantId(participant);
        const currentState = {
          audioEnabled: participant.audioEnabled,
          hasAudioTrack: !!participant.audioTrack
        };

        // Get previous state
        const prevState = audioStateMap.get(participant.id);

        // If we have a previous state and it's different from current state, handle the change
        if (prevState &&
          (prevState.audioEnabled !== currentState.audioEnabled ||
            prevState.hasAudioTrack !== currentState.hasAudioTrack)) {

          logger.info('Detected audio state change without event', {
            participantId,
            name: participant.name,
            prevEnabled: prevState.audioEnabled,
            newEnabled: currentState.audioEnabled,
            prevHasTrack: prevState.hasAudioTrack,
            newHasTrack: currentState.hasAudioTrack
          });

          // Handle unmute
          if (currentState.audioEnabled && currentState.hasAudioTrack &&
            (!prevState.audioEnabled || !prevState.hasAudioTrack)) {
            if (!activeResources.current.has(participantId) &&
              !initializingParticipants.has(participantId) &&
              participant.audioTrack) {
              logger.info('Setting up audio after detecting unmute', {
                participantId,
                classId
              });
              void setupAudioProcessingCallback(participant, participant.audioTrack, 0);
            }
          }
          // Handle mute
          else if ((!currentState.audioEnabled || !currentState.hasAudioTrack) &&
            (prevState.audioEnabled && prevState.hasAudioTrack)) {
            if (activeResources.current.has(participantId)) {
              logger.info('Cleaning up audio after detecting mute', {
                participantId,
                classId
              });
              void cleanupAudioProcessing(participantId);
            }
          }
        }

        // Update the state map
        audioStateMap.set(participant.id, currentState);
      });
    };

    // Initialize the state map
    joinedParticipants.forEach(participant => {
      if (!participant?.id || participant.id === (meeting as any).self.id) return;

      audioStateMap.set(participant.id, {
        audioEnabled: participant.audioEnabled,
        hasAudioTrack: !!participant.audioTrack
      });
    });

    // Check for changes every 1 second (aggressive polling to catch any mute/unmute)
    const intervalId = setInterval(checkForMuteChanges, 1000);

    return () => clearInterval(intervalId);
  }, [joinedParticipants, meeting, setupAudioProcessingCallback, cleanupAudioProcessing, getParticipantId, classId]);

  // Initialize the worker
  useEffect(() => {
    if (!meeting) return;

    // Initialize worker if needed
    if (!workerRef.current) {
      workerRef.current = getAudioWorker();

      // Set up worker message handler
      workerRef.current.onmessage = (e) => {
        const {
          type,
          message,
          data,
          processingId,
          participantId,
          userType,
          userId,
          audioData,
          bufferTimestamp,
        } = e.data;

        switch (type) {
          case 'log':
            // Handle logs from worker
            if (e.data.logType === 'info') {
              logger.info(message, data);
            } else if (e.data.logType === 'error') {
              logger.error(message, data);
            }
            break;

          case 'processAudio':
            // Process audio data when worker tells us to
            if (processingId && participantId && audioData) {
              // A chunk queued before their client took over must not be sent
              // either: it would be transcribed a second time.
              if (isClientCaptured(userId)) return;
              (async () => {
                try {
                  // This handler is attached once, so read the latest list.
                  const validParticipants = monitorRef.current.joinedParticipants
                    .map((p) => p.name)
                    .filter((name): name is string => !!name);

                  const request_id = `${userType}-${classId}-${userId}-${Date.now()}`;

                  const float32In = new Float32Array(audioData as ArrayBuffer);
                  let audioToEncode: Float32Array;

                  if (CONFIG.ENABLE_VAD) {
                    // 1. VAD on raw 48kHz → trim to speech only
                    const { hasSpeech, segments } = await vadCheckSpeech(float32In, CONFIG.SAMPLE_RATE);
                    if (!hasSpeech) {
                      logger.info('VAD: no speech detected, skipping upload', {
                        participantId,
                        classId,
                      });
                      return;
                    }
                    // Use trimmed 16kHz audio from VAD segments (fallback to downsample on error)
                    audioToEncode = segments.length > 0
                      ? trimAudio(segments)
                      : downsample(float32In, CONFIG.SAMPLE_RATE, 16000);
                  } else {
                    // No VAD — downsample and pass through
                    audioToEncode = downsample(float32In, CONFIG.SAMPLE_RATE, 16000);
                  }

                  // 2. Ask for a presigned URL in the format we would prefer.
                  // The extension and Content-Type are both signed into that URL,
                  // so the SERVER's answer — not our preference — decides what we
                  // encode. A data-vortex that predates Opus support ignores the
                  // param and hands back a .wav key, and we must honour that or we
                  // would upload Opus bytes under a .wav key and break transcription.
                  const requestUploadUrl = async (format: 'opus' | 'wav') => {
                    const res = await fetch(
                      `${CONFIG.API_ENDPOINT}/v1/audio-upload-url?classId=${classId}&userId=${userId}&sessionId=${participantId}&format=${format}`,
                    );
                    if (!res.ok) throw new Error('Failed to get presigned upload URL');
                    return res.json();
                  };

                  const toWav = (pcm: Float32Array): ArrayBuffer => {
                    const int16 = new Int16Array(pcm.length);
                    for (let i = 0; i < pcm.length; i++) {
                      int16[i] = Math.max(-32768, Math.min(32767, Math.floor(pcm[i] * 32767)));
                    }
                    return encodeWAV(int16, 16000);
                  };

                  const canOpus = await isOpusEncodingSupported(16000);
                  let upload = await requestUploadUrl(canOpus ? 'opus' : 'wav');

                  // 3. Encode to match the key we were given. Opus is ~7x smaller
                  // than 16kHz PCM WAV for the same speech, which cuts this upload
                  // and the transcription Lambda's egress to Gemini.
                  let body: ArrayBuffer;
                  if (typeof upload.s3Key === 'string' && upload.s3Key.endsWith('.opus')) {
                    const opusBuffer = await encodeOpus(audioToEncode, 16000);
                    if (opusBuffer) {
                      body = opusBuffer.buffer.slice(
                        opusBuffer.byteOffset,
                        opusBuffer.byteOffset + opusBuffer.byteLength,
                      ) as ArrayBuffer;
                    } else {
                      // The encoder failed after the server signed a .opus key. We
                      // cannot put WAV bytes there, so take a fresh .wav URL rather
                      // than drop the turn — if the encoder is failing systematically
                      // this is the difference between degraded and no transcription.
                      logger.error('Opus encode failed, falling back to WAV upload', {
                        participantId,
                        classId,
                      });
                      upload = await requestUploadUrl('wav');
                      body = toWav(audioToEncode);
                    }
                  } else {
                    body = toWav(audioToEncode);
                  }

                  // 4. PUT directly to S3 (recording-app → S3, no data-vortex in data path).
                  // Content-Type is signed into the URL, so it must match exactly.
                  // Older builds do not return contentType — they signed audio/wav.
                  const putRes = await fetch(upload.presignedUrl, {
                    method: 'PUT',
                    body,
                    headers: { 'Content-Type': upload.contentType || 'audio/wav' },
                  });
                  if (!putRes.ok) throw new Error(`S3 PUT failed: ${putRes.status}`);
                  const { s3Key } = upload;

                  // 6. Notify data-vortex with lightweight metadata only (~200 bytes)
                  await fetch(`${CONFIG.API_ENDPOINT}/v1/process-audio-s3`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      s3_key: s3Key,
                      session_id: participantId,
                      sample_rate: 16000,
                      user_type: userType,
                      class_id: classId,
                      user_id: userId,
                      participant_names: validParticipants,
                      request_id,
                      buffer_timestamp: bufferTimestamp || null,
                    }),
                  });
                } catch (error) {
                  logger.error('Audio upload pipeline error', {
                    processingId,
                    error: error instanceof Error ? error.message : String(error),
                    participantId,
                    classId,
                  });
                } finally {
                  if (workerRef.current) {
                    workerRef.current.postMessage({
                      action: 'processingComplete',
                      participantId,
                    });
                  }
                }
              })();
            }
            break;
        }
      };
    }

    return () => {
      // Worker cleanup is handled by the singleton pattern
    };
  }, [meeting, joinedParticipants, classId, isClientCaptured]);

  // Handle participant changes
  useEffect(() => {
    if (!meeting || !joinedParticipants?.length) {
      return;
    }

    // Track current participants
    const currentParticipantIds = new Set(joinedParticipants.map((p) => p.id));

    // Find participants that have left
    const removedParticipantIds = Array.from(handledParticipants.current).filter(
      (id) => !currentParticipantIds.has(id),
    );

    // Clean up removed participants
    removedParticipantIds.forEach((participantId) => {
      handledParticipants.current.delete(participantId);

      // Use the mapping to get the custom ID
      const customId = participantIdMapping.current.get(participantId);
      if (customId) {
        void cleanupAudioProcessing(customId);
        // Remove from mapping after cleanup
        participantIdMapping.current.delete(participantId);
      }

      // Remove event handler
      const handler = audioUpdateHandlersRef.current.get(participantId);
      if (handler) {
        audioUpdateHandlersRef.current.delete(participantId);
      }
    });

    // Set up handlers for all participants
    // First, remove any existing audio handlers and set up fresh ones for everyone
    joinedParticipants.forEach((participant) => {
      // Skip self-participant
      if (!participant?.id || participant.id === (meeting as any).self.id) {
        return;
      }

      const participantId = getParticipantId(participant);

      // If we already have a handler for this participant, remove it first
      // This ensures we always have the latest reference to the participant
      const existingHandler = audioUpdateHandlersRef.current.get(participant.id);
      if (existingHandler) {
        try {
          // Type cast to any for Dyte-specific event methods
          (participant as any).off('audioUpdate', existingHandler);
        } catch (error) {
          // Just log the error and continue
          logger.error('Error removing audio handler', {
            participantId,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }

      // Create a fresh handler for audio updates
      const audioUpdateHandler = (payload: {
        audioEnabled: boolean;
        audioTrack: MediaStreamTrack;
      }) => {
        logger.info('Audio update event received', {
          participantId,
          audioEnabled: payload.audioEnabled,
          hasTrack: !!payload.audioTrack,
          classId,
          name: participant.name,
          userType: participant.presetName === 'group_call_host' ? 'tutor' : 'student'
        });

        // Always clean up existing processing first to ensure a fresh start
        if (activeResources.current.has(participantId)) {
          logger.info('Cleaning up existing audio processing before potential restart', {
            participantId,
            classId
          });
          void cleanupAudioProcessing(participantId);
        }

        if (payload.audioEnabled && payload.audioTrack) {
          // Set up audio processing when unmuted with valid track
          // Wait a small delay to ensure the track is fully available
          setTimeout(() => {
            if (!activeResources.current.has(participantId) &&
              !initializingParticipants.has(participantId)) {
              logger.info('Setting up audio from update handler after unmute', {
                participantId,
                classId,
                name: participant.name
              });
              void setupAudioProcessingCallback(participant, payload.audioTrack, 0);
            }
          }, 100);
        }
      };

      // Register the new handler and update our records
      try {
        // Type cast to any for Dyte-specific event methods
        (participant as any).on('audioUpdate', audioUpdateHandler);
        audioUpdateHandlersRef.current.set(participant.id, audioUpdateHandler);
        handledParticipants.current.add(participant.id);
        participantIdMapping.current.set(participant.id, participantId);

        logger.info('Audio update handler registered', {
          participantId,
          name: participant.name,
          userType: participant.presetName === 'group_call_host' ? 'tutor' : 'student'
        });
      } catch (error) {
        logger.error('Failed to register audio update handler', {
          participantId,
          error: error instanceof Error ? error.message : String(error)
        });
      }

      // CRITICAL FIX: Check initial state for ALL participants, not just newly handled ones
      // This ensures we capture participants who join with audio already enabled
      if (participant.audioEnabled && participant.audioTrack) {
        // Check if this participant should have audio processing but doesn't
        if (!activeResources.current.has(participantId) &&
          !initializingParticipants.has(participantId)) {
          logger.info('Initial setup for already unmuted participant', {
            participantId,
            classId,
            name: participant.name,
          });
          void setupAudioProcessingCallback(participant, participant.audioTrack, 0);
        }
      }
    });

    // Cleanup function to remove handlers
    return () => {
      joinedParticipants.forEach((participant) => {
        if (!participant?.id) return;

        const handler = audioUpdateHandlersRef.current.get(participant.id);
        if (handler) {
          // Type cast to any for Dyte-specific event methods
          (participant as any).off('audioUpdate', handler);
        }
      });
    };
  }, [joinedParticipants, meeting, cleanupAudioProcessing, setupAudioProcessingCallback, getParticipantId, classId]);

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      // Clean up all active resources
      const cleanupPromises = Array.from(activeResources.current.keys()).map((participantId) =>
        cleanupAudioProcessing(participantId),
      );

      Promise.all(cleanupPromises)
        .then(() => {
          logger.info('All participants cleaned up during unmount', { classId });
        })
        .catch((error) => {
          logger.error('Error during unmount cleanup', {
            error: error instanceof Error ? error.message : String(error),
            classId,
          });
        });
    };
  }, [cleanupAudioProcessing, classId]);

  return null;
};

export default useStudentTalkTime;